<template>
    <div>
        <b-datatable
            ref="grid"
            :key="datatableKey"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            :initial-filters="initialFilters"
            has-filter-selector
            item-key="id"
            notification
        >
            <template #filters="{ filters }">
                <v-col cols="4">
                    <b-filter-input
                        v-model="filters.filters.device_id"
                        :label="t('quality-control::grid.label.device_id')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.is_patient_mode"
                        :items="filtersOptions.filter_is_patient_mode"
                        :label="t('quality-control::grid.label.device_mode')"
                        :clearable="filters.filters.is_patient_mode !== null"
                    >
                    </b-filter-select>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.device"
                        :items="filtersOptions.device"
                        :label="'Device type'"
                        :clearable="filters.filters.active !== null"
                    >
                    </b-filter-select>
                </v-col>
            </template>

            <template #[`item.device_id`]="{ item }">
                <span v-if="item.device == 'pixcell'">{{ item.device_id }}</span>
                <span v-else class="link" @click="$root.goTo(detailsUrl(item))">{{ item.device_id }}</span>
            </template>

            <template #[`item.is_patient_mode`]="{ item }">
                <div v-if="item.device !== 'pixcell'" class="grid-status">
                    <span
                        v-html="
                            !item.is_patient_mode
                                ? t('quality-control::device_mode.QC')
                                : t('quality-control::device_mode.patient')
                        "
                    ></span>
                </div>
                <div v-else class="grid-status">
                    <v-tooltip v-if="item.neqas_overdue" top>
                        <template #activator="{ on, attrs }">
                            <span v-bind="attrs" class="red--text" v-on="on">Overdue</span>
                        </template>
                        <span>Current NEQAS probes are overdue.</span>
                    </v-tooltip>
                </div>
            </template>

            <template #[`item.pharmacy_site`]="{ item }">
                <span>{{ item.pharmacy_site.site_name }}</span>
            </template>

            <template #[`item.device`]="{ item }">
                <span>{{ item.device ? item.device.charAt(0).toUpperCase() + item.device.slice(1) : '' }}</span>
            </template>

            <template #[`item.operator`]="{ item }">
                <span :class="!item.operator ? 'red--text' : ''">{{
                    item.operator ? item.operator.name : t('quality-control::grid.label.not_assigned')
                }}</span>
            </template>

            <template #[`item.id`]="{ item }">
                <div class="table-actions">
                    <v-btn
                        v-if="can('api.v1.providers.assign') && !isOperatorAssigned"
                        class="mr-2 full-size-btn"
                        outlined
                        rounded
                        text
                        color="#fff"
                        @click="assignToProvider(item)"
                    >
                        {{
                            checkCurrentUserEqualToDeviceOperator(item)
                                ? t('quality-control::button.unassign_from_me')
                                : !item.operator
                                ? t('quality-control::button.assign_to_me')
                                : t('quality-control::button.re_assign_to_me')
                        }}
                    </v-btn>
                    <b-confirm
                        :title="t('quality-control::confirmation.title.assign')"
                        :text="
                            checkCurrentUserEqualToDeviceOperator(item)
                                ? t('quality-control::confirmation.messages.unassign')
                                : assingMessage(item)
                        "
                        @true="assignToProvider(item)"
                    >
                        <template #button="{ on }">
                            <v-btn
                                v-if="can('api.v1.providers.assign') && isOperatorAssigned"
                                class="mr-2 full-size-btn"
                                outlined
                                rounded
                                text
                                color="#fff"
                                v-on="on"
                            >
                                {{
                                    checkCurrentUserEqualToDeviceOperator(item)
                                        ? t('quality-control::button.unassign_from_me')
                                        : !item.operator
                                        ? t('quality-control::button.assign_to_me')
                                        : t('quality-control::button.re_assign_to_me')
                                }}
                            </v-btn>
                        </template>
                    </b-confirm>
                    <v-tooltip v-if="item.device !== 'pixcell'" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn icon v-bind="attrs" v-on="on" @click="$root.goTo(detailsUrl(item))">
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('quality-control::button.samples') }}</span>
                    </v-tooltip>
                    <template v-else>
                        <span class="v-btn v-btn--icon v-btn--round theme--light v-size--default"></span>
                    </template>
                </div>
            </template>
        </b-datatable>
    </div>
</template>
<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
        initialFilters: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isOperatorAssigned: false,
            currentUserDevice: null,
            isUserCurrentDeviceOperator: false,
            datatableKey: 0,
            hiddenColumns: ['name', 'neqas_overdue'],
            columns: [
                {
                    text: '',
                    value: 'name',
                },
                {
                    text: t('quality-control::grid.label.device_id'),
                    value: 'device_id',
                    width: 200,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.device_mode'),
                    value: 'is_patient_mode',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.device_type'),
                    value: 'device',
                    width: 140,
                    sortable: true,
                },
                {
                    text: 'neqas overdue',
                    value: 'neqas_overdue',
                },
                {
                    text: t('quality-control::grid.label.pharmacy_site'),
                    value: 'pharmacy_site.site_name',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.assigned_to'),
                    value: 'operator',
                    width: 140,
                    sortable: true,
                },
                {
                    text: '',
                    value: 'id',
                    sortable: false,
                    width: 210,
                    align: 'end',
                },
            ],
            filtersOptions: {
                device: [
                    {
                        text: t('astm-devices::options.status.all'),
                        value: null,
                    },
                    {
                        text: 'Pixcell',
                        value: 'pixcell',
                    },
                    {
                        text: 'Yumizen',
                        value: 'yumizen',
                    },
                ],
                filter_is_patient_mode: [
                    {
                        text: t('default::options.all'),
                        value: null,
                    },
                    {
                        text: t('quality-control::options.quality_control_mode'),
                        value: 0,
                    },
                    {
                        text: t('quality-control::options.patients_mode'),
                        value: 1,
                    },
                ],
            },
        };
    },
    computed: {
        userId() {
            let userData = User || null;
            if (!userData) {
                return null;
            }
            return User.id;
        },
    },
    created() {
        this.getAssignedOperators();
        this.$store.dispatch('qualityControl/fetchMyDevices', this.userId);
    },
    mounted() {},
    methods: {
        assingMessage(item) {
            let message = t('quality-control::confirmation.messages.assign');
            if (!this.currentUserDevice) {
                return message;
            }
            const newMsg = message
                .replaceAll('%2%', `<strong>${item.device_id}</strong>`)
                .replaceAll('%1%', `<strong>${this.currentUserDevice.device_id}</strong>`);
            return newMsg;
        },
        getAssignedOperators() {
            axios({
                method: 'get',
                url: route('api.v1.quality-control.index', {
                    operator_id: this.userId,
                    visible_in_qc_list: 1,
                    fields: 'id,device_id',
                }),
            })
                .then((result) => {
                    this.isOperatorAssigned = !!result.data.data.length;
                    if (result.data.data.length) {
                        this.currentUserDevice = result.data.data[0];
                    }
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                });
        },
        assignToProvider(item) {
            axios({
                method: 'put',
                url: route('api.v1.providers.assign', { blood_result_provider: item.id }),
            })
                .then((success) => {
                    this.getAssignedOperators();
                    this.$root.notify('info', t('default::notification.success'), success.data.operator);
                    if (success.data.neqas_messages && success.data.neqas_messages.length) {
                        _.delay(() => {
                            this.$root.notify(
                                'warning',
                                t('default::notification.success'),
                                `${Object.values(success.data.neqas_messages).join('<br/>')}`
                            );
                        }, 300);
                    }
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    this.datatableKey++;
                    this.$store.dispatch('qualityControl/fetchBlockedDeviceList');
                    this.$store.dispatch('qualityControl/fetchHoribaSamplesProvider', item.id);
                });
        },
        checkCurrentUserEqualToDeviceOperator(item) {
            if (item.operator && item.operator.id) {
                return (this.isUserCurrentDeviceOperator = item.operator.id === this.userId ? true : false);
            }
        },
        detailsUrl(item) {
            this.checkCurrentUserEqualToDeviceOperator(item);
            return route('horiba-samples.index', {
                provider_id: item.id,
            });
        },
    },
};
</script>
<style scoped></style>
